if (!customElements.get('idw-product-options-ready')) {
  customElements.define('idw-product-options-ready', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.state = 'loading';
      this.productInfo = this.closest('product-info');
      this.form = document.getElementById(this.dataset.formId);
      if (!this.productInfo) return;
      if (!this.form) {
        this.waitingObserver = new MutationObserver(() => {
          if (!document.getElementById(this.dataset.formId)) return;
          this.waitingObserver.disconnect();
          this.controller.abort();
          this.controller = null;
          this.connectedCallback();
        });
        this.waitingObserver.observe(this.productInfo, { childList: true, subtree: true });
        return;
      }
      this.productForm = this.form.closest('product-form');
      if (this.hasNoApplicableOptions()) {
        this.ready();
        return;
      }
      const options = { capture: true, signal: this.controller.signal };
      if (this.dataset.productId === '7242293051456') {
        this.productInfo.addEventListener('click', this.navigateToMissingOption.bind(this), options);
        this.productInfo.addEventListener('submit', this.navigateToMissingOption.bind(this), options);
      }
      this.productInfo.addEventListener('click', this.blockPurchase.bind(this), options);
      this.productInfo.addEventListener('submit', this.blockPurchase.bind(this), options);
      document.addEventListener('gpoRenderCompleted', (event) => {
        const detail = event.detail || {};
        if (detail.form !== this.form || String(detail.product?.id) !== this.dataset.productId) return;
        this.ready();
      }, { signal: this.controller.signal });
      this.productInfo.addEventListener('idw:variant-request-state', () => {
        if (this.state === 'ready') this.releaseOwnedButtons();
        else this.lockButtons();
      }, { signal: this.controller.signal });
      this.querySelector('[data-idw-options-reload]')?.addEventListener('click', () => {
        window.location.reload();
      }, { signal: this.controller.signal });
      this.observer = new MutationObserver(() => {
        if (this.isVendorReady()) this.ready();
        else if (this.state !== 'ready') this.lockButtons();
      });
      this.observer.observe(this.productInfo, { childList: true, subtree: true });
      this.bodyObserver = new MutationObserver(() => {
        if (this.isVendorReady()) this.ready();
      });
      this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['data-gpo-is-enabled'] });
      this.timeout = setTimeout(() => {
        if (this.state === 'ready') return;
        this.state = 'error';
        if (this.productForm) this.productForm.dataset.idwOptionsState = 'error';
        this.setAttribute('aria-busy', 'false');
        this.querySelector('[data-idw-options-message]').textContent = this.dataset.errorText;
        this.querySelector('[data-idw-options-recovery]').hidden = false;
      }, 20000);
      this.lockButtons();
      if (this.isVendorReady()) this.ready();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.bodyObserver?.disconnect();
      this.waitingObserver?.disconnect();
      cancelAnimationFrame(this.invalidNavigationFrame);
      clearTimeout(this.timeout);
    }

    // Let Globo validate first; only adjust the first visible missing field's position.
    navigateToMissingOption(event) {
      const purchase = event.type === 'submit'
        ? event.target === this.form
        : event.target.closest('[name="add"], .gpo-clone-atc-button, .gpo-clone-stickyAtc-button');
      if (!purchase || this.state !== 'ready') return;
      cancelAnimationFrame(this.invalidNavigationFrame);
      this.invalidNavigationFrame = requestAnimationFrame(() => {
        this.invalidNavigationFrame = requestAnimationFrame(() => {
          this.invalidNavigationFrame = null;
          const field = Array.from(this.productInfo.querySelectorAll('.gpo-element.has-error'))
            .find((element) => element.getBoundingClientRect().height > 0
              && element.querySelector('.help-block.error'));
          if (!field) return;
          field.style.setProperty('scroll-margin-top', 'calc(var(--header-height, 0px) + 16px)');
          field.scrollIntoView({ block: 'start', behavior: 'smooth' });
        });
      });
    }

    hasNoApplicableOptions() {
      const config = window.GPOConfigs;
      return config?.optionIds === 'empty'
        && String(config.product?.id) === this.dataset.productId
        && (!config.form || config.form === this.form);
    }

    isVendorReady() {
      const config = window.GPOConfigs;
      if (this.hasNoApplicableOptions()) return true;
      if (config?.form !== this.form || String(config.product?.id) !== this.dataset.productId) return false;
      if (document.body.getAttribute('data-gpo-is-enabled') !== 'true') return false;
      if (typeof config.handleValidate !== 'function') return false;
      const app = this.productInfo.querySelector('.gpo-app[data-option-set-id]');
      const setId = app?.getAttribute('data-option-set-id');
      const marker = this.form.querySelector('input[name="properties[_has_gpo]"]');
      return Boolean(setId && String(config.option?.id) === setId && marker?.value === setId);
    }

    lockButtons() {
      if (this.productForm && this.productForm.dataset.idwOptionsState !== this.state) {
        this.productForm.dataset.idwOptionsState = this.state;
      }
      this.form.querySelectorAll('[name="add"], .gpo-clone-atc-button').forEach((button) => {
        if (!button.hasAttribute('data-idw-options-locked')) {
          button.dataset.idwOptionsLocked = 'true';
          button.dataset.idwNativeDisabled = String(button.disabled);
        }
        if (!button.disabled) button.disabled = true;
      });
      this.form.querySelectorAll('[data-idw-dynamic-checkout]').forEach((element) => {
        if (!element.hidden) element.hidden = true;
        element.inert = true;
      });
    }

    blockPurchase(event) {
      if (this.state === 'ready') return;
      const purchase = event.type === 'submit'
        ? event.target === this.form
        : event.target.closest('product-form [type="submit"], .gpo-clone-atc-button, .gpo-clone-stickyAtc-button, .shopify-payment-button');
      if (!purchase) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.state === 'error') this.focus();
    }

    releaseOwnedButtons() {
      if (['loading', 'error'].includes(this.productInfo.dataset.idwVariantState)) return;
      const selected = this.productInfo.querySelector('variant-selects [data-selected-variant]');
      let available;
      if (selected) {
        try {
          const variant = JSON.parse(selected.textContent);
          available = Boolean(variant?.available);
        } catch {
          available = false;
        }
      }
      const originalDisabled = this.productForm?.submitButton?.dataset.idwNativeDisabled;
      this.form.querySelectorAll('[data-idw-options-locked]').forEach((button) => {
        const nativeDisabled = originalDisabled ?? button.dataset.idwNativeDisabled;
        const disabled = nativeDisabled === 'true' || available === false;
        if (!button.classList.contains('gpo-loading')) button.disabled = disabled;
        delete button.dataset.idwOptionsLocked;
        delete button.dataset.idwNativeDisabled;
      });
      this.form.querySelectorAll('[data-idw-dynamic-checkout]').forEach((element) => {
        element.hidden = false;
        element.inert = false;
      });
    }

    ready() {
      if (this.state === 'ready') return;
      this.state = 'ready';
      if (this.productForm) this.productForm.dataset.idwOptionsState = 'ready';
      clearTimeout(this.timeout);
      this.observer?.disconnect();
      this.bodyObserver?.disconnect();
      this.setAttribute('aria-busy', 'false');
      this.hidden = true;
      this.releaseOwnedButtons();
    }
  });
}
